/**
 * Pure model for the shared Meridian ``<DataTable>`` (ADR-038 Phase E).
 *
 * The DOM-free half of the component: the column descriptor, the
 * multi-sort reducer, the column-visibility invariant, the column-order
 * resolution + move, and the pagination arithmetic. No React, no
 * ``@tanstack/react-table`` — kept separate so the *policy* decisions
 * Meridian owns stay unit-testable without a render, and so the library
 * inside :func:`DataTable` remains an implementation detail (feature-spec
 * §4.1: "the deliverable is Meridian's own component; the library
 * supplies the state machinery inside it").
 *
 * # Why the sort reducer lives here rather than in the library
 *
 * ``@tanstack/react-table``'s built-in ``toggleSorting`` cycles
 * none → asc → desc → none from a single control. REQ-UI-120 specifies a
 * different gesture — a chevron *per direction*, where re-activating the
 * already-active chevron clears that column's sort (architect direction,
 * 2026-07-25). :func:`nextSortState` encodes that gesture; the resolved
 * array is handed to the table as controlled ``sorting`` state, so the
 * library still supplies the header/column model, sizing, ordering, and
 * visibility machinery while Meridian owns the interaction contract.
 *
 * Cross-references:
 *   - REQ-UI-118..122 (results table: columns, visibility, multi-sort,
 *     resize/reorder, pagination).
 *   - Feature spec §2.10 (table behaviours) + D6 (bounded, honest totals)
 *     + D7 (≤3 sort columns, server-computed order).
 *   - ADR-038 §Decision 5 (the shared ``<DataTable>``).
 */
import type { ReactNode } from "react";

/** Sort direction — matches the BFF's ``sort[].direction`` wire value. */
export type SortDirection = "asc" | "desc";

/** One sort key. ``key`` is the column key, which is also the attribute name. */
export interface SortRule {
  readonly key: string;
  readonly direction: SortDirection;
}

/**
 * The ordered sort specification. Position is significance: index 0 is
 * the primary key. Sent to the BFF verbatim as ``sort`` (D7); the server
 * appends the type's natural-key tiebreaker chain so offset pages stay
 * stable under concurrent inserts.
 */
export type SortState = readonly SortRule[];

/**
 * Maximum simultaneous sort columns (D7 / ``maxMultiSortColCount``).
 *
 * The BFF rejects more than three sort keys, so the cap is a contract,
 * not a preference: appending a fourth is refused here rather than sent
 * and 400'd.
 */
export const MAX_SORT_COLUMNS = 3;

/**
 * One column of a :func:`DataTable`.
 *
 * ``render`` receives the whole row so a cell can compose several
 * attributes (e.g. a component id + instance id) without the table
 * needing to know the row shape. ``sortable`` mirrors the catalog's
 * per-attribute ``sortable`` flag — an unsortable column renders no
 * chevrons and carries no ``aria-sort`` (REQ-UI-059).
 */
export interface DataTableColumn<TRow> {
  /** Stable column id. Also the BFF attribute name for sortable columns. */
  readonly key: string;
  /** Header label. */
  readonly label: string;
  /** Whether the column offers sort chevrons. Defaults to ``false``. */
  readonly sortable?: boolean;
  /** Whether the column shows before any persisted visibility state. */
  readonly defaultVisible?: boolean;
  /** Initial width in pixels. The operator's drag-resize overrides it. */
  readonly width?: number;
  /** Minimum width in pixels enforced during a resize drag. */
  readonly minWidth?: number;
  /** Right-align numeric columns; defaults to left. */
  readonly align?: "left" | "right";
  /**
   * Optional advisory shown as the header's ``title`` — e.g. the
   * unindexed-attribute hint the catalog drives (feature spec §2.11).
   */
  readonly headerTitle?: string;
  /** Render this column's cell for one row. */
  readonly render: (row: TRow) => ReactNode;
}

// ---- sort ---------------------------------------------------------

/**
 * Resolve the next sort state for a chevron activation (REQ-UI-120).
 *
 * Gesture contract:
 *
 * - Activating a chevron on a column **not** sorted in that direction
 *   selects that direction. Without the modifier the sort is *replaced*;
 *   with it (Ctrl/Cmd-click) the column is appended as a subsequent key,
 *   or flipped in place when it is already a sort key — flipping in place
 *   preserves the column's ordinal, which is what the operator expects
 *   when they are refining an existing multi-sort.
 * - Re-activating the **already-active** chevron clears *that column's*
 *   sort and leaves the remaining keys in order, modifier or not. The
 *   requirement says "clears that column's sort", not "clears the sort".
 *
 * Refusal is explicit rather than silent: appending past ``maxColumns``
 * returns ``current`` **by reference** so the caller can tell the
 * difference between "nothing changed" and "your click was dropped" and
 * surface the cap to the operator (CLAUDE.md §8.1 — operator visibility).
 *
 * Pure; never mutates ``current``.
 */
export function nextSortState(
  current: SortState,
  key: string,
  direction: SortDirection,
  additive: boolean,
  maxColumns: number = MAX_SORT_COLUMNS,
): SortState {
  const existing = current.find((rule) => rule.key === key);

  // Re-activating the active chevron clears just this column.
  if (existing !== undefined && existing.direction === direction) {
    return current.filter((rule) => rule.key !== key);
  }

  // Flip an existing key in place — the ordinal is preserved whether or
  // not the modifier is held, because the column is already a sort key.
  if (existing !== undefined) {
    if (!additive) {
      return [{ key, direction }];
    }
    return current.map((rule) => (rule.key === key ? { key, direction } : rule));
  }

  if (!additive) {
    return [{ key, direction }];
  }

  // Appending a new key — refuse at the cap rather than silently
  // truncating or evicting a key the operator chose earlier.
  if (current.length >= maxColumns) {
    return current;
  }
  return [...current, { key, direction }];
}

/** The 1-based sort ordinal for a column, or ``null`` when unsorted. */
export function sortOrdinal(state: SortState, key: string): number | null {
  const index = state.findIndex((rule) => rule.key === key);
  return index === -1 ? null : index + 1;
}

/** The direction a column is sorted in, or ``null`` when unsorted. */
export function isSortedBy(state: SortState, key: string): SortDirection | null {
  return state.find((rule) => rule.key === key)?.direction ?? null;
}

// ---- column visibility --------------------------------------------

/**
 * Toggle one column key, enforcing the **min-1-column** invariant
 * (REQ-UI-119): the last visible column cannot be hidden. Generalizes
 * :func:`audit-display.toggleColumnKey` over an arbitrary column set.
 *
 * Pure — returns a new set; the caller persists and sets state.
 */
export function toggleColumnVisibility(current: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(current);
  if (next.has(key)) {
    if (next.size > 1) next.delete(key); // never empty the table
  } else {
    next.add(key);
  }
  return next;
}

/**
 * The seed visible-column set for a column list — those marked
 * ``defaultVisible``, falling back to the first column when the list
 * marks none. The min-1 invariant applies to the seed as well as to the
 * toggle, so a catalog that forgets ``default_visible`` still yields a
 * table with a column in it rather than an empty grid.
 */
export function defaultVisibleColumnKeys<TRow>(
  columns: readonly DataTableColumn<TRow>[],
): Set<string> {
  const marked = columns.filter((col) => col.defaultVisible === true);
  if (marked.length > 0) {
    return new Set(marked.map((col) => col.key));
  }
  const first = columns[0];
  return first === undefined ? new Set<string>() : new Set([first.key]);
}

// ---- column order -------------------------------------------------

/**
 * Resolve the effective left-to-right column order from the declared
 * columns and an optional persisted order.
 *
 * Defensive at the persistence trust boundary (coding-standards §2.2):
 * persisted keys that no longer exist are dropped, duplicates collapse,
 * and declared keys absent from the persisted order are appended in
 * declaration order. A stale Profile-stored order from a catalog that has
 * since gained or lost an attribute therefore neither hides a new column
 * nor resurrects a removed one.
 */
export function resolveColumnOrder<TRow>(
  columns: readonly DataTableColumn<TRow>[],
  persisted: readonly string[] | undefined,
): readonly string[] {
  const declared = columns.map((col) => col.key);
  if (persisted === undefined) return declared;

  const known = new Set(declared);
  const seen = new Set<string>();
  const resolved: string[] = [];
  for (const key of persisted) {
    if (known.has(key) && !seen.has(key)) {
      seen.add(key);
      resolved.push(key);
    }
  }
  for (const key of declared) {
    if (!seen.has(key)) resolved.push(key);
  }
  return resolved;
}

/**
 * Move ``movedKey`` to ``targetKey``'s position — the drop half of the
 * native HTML5 header drag (REQ-UI-121). The moved column takes the
 * target's index and the intervening columns shift toward where the
 * moved column came from.
 *
 * Returns the input **by reference** when the move is a no-op (same key,
 * or either key unknown) so React consumers skip a needless re-render.
 */
export function moveColumnKey(
  order: readonly string[],
  movedKey: string,
  targetKey: string,
): readonly string[] {
  if (movedKey === targetKey) return order;
  const from = order.indexOf(movedKey);
  const to = order.indexOf(targetKey);
  if (from === -1 || to === -1) return order;
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, movedKey);
  return next;
}

/** The visible columns, in the resolved order — what the table renders. */
export function orderedVisibleColumns<TRow>(
  columns: readonly DataTableColumn<TRow>[],
  order: readonly string[],
  visible: ReadonlySet<string>,
): readonly DataTableColumn<TRow>[] {
  const byKey = new Map(columns.map((col) => [col.key, col]));
  const resolved: DataTableColumn<TRow>[] = [];
  for (const key of order) {
    const col = byKey.get(key);
    if (col !== undefined && visible.has(key)) resolved.push(col);
  }
  return resolved;
}

// ---- pagination ---------------------------------------------------

/** Number of pages for a total at a page size. Always at least 1. */
export function pageCount(total: number, pageSize: number): number {
  if (pageSize <= 0) return 1;
  return Math.max(1, Math.ceil(total / pageSize));
}

/** Clamp a (possibly operator-typed) page index into ``[0, count - 1]``. */
export function clampPageIndex(index: number, count: number): number {
  if (!Number.isFinite(index)) return 0;
  return Math.min(Math.max(Math.trunc(index), 0), Math.max(count - 1, 0));
}

/**
 * The windowed page-number strip around the current page (REQ-UI-122),
 * modeled on Appaloosa's ``Pagination``. Anchors at either end rather
 * than centring past the boundary, so the strip is always ``size`` wide
 * when there are that many pages.
 */
export function pageNumberWindow(
  pageIndex: number,
  count: number,
  size: number,
): readonly number[] {
  if (count <= size) {
    return Array.from({ length: count }, (_, i) => i);
  }
  const half = Math.floor(size / 2);
  const start = Math.min(Math.max(pageIndex - half, 0), count - size);
  return Array.from({ length: size }, (_, i) => start + i);
}

/**
 * Render a total for display, honouring D6's lower-bound contract: past
 * the server's count ceiling the number is a floor, never an exact
 * count, and must read as such.
 */
export function totalLabel(total: number, isLowerBound: boolean): string {
  const formatted = total.toLocaleString("en-US");
  return isLowerBound ? `${formatted}+` : formatted;
}

/**
 * "1–25 of 130" / "1–25 of 100,000+" / "No records" — the pagination
 * footer's item-range label. ``itemsOnPage`` (not the page size) sizes
 * the window so a short final page reads correctly.
 */
export function itemRangeLabel(
  pageIndex: number,
  pageSize: number,
  itemsOnPage: number,
  total: number,
  totalIsLowerBound: boolean,
): string {
  if (itemsOnPage === 0) return "No records";
  const first = pageIndex * pageSize + 1;
  const last = first + itemsOnPage - 1;
  return (
    `${first.toLocaleString("en-US")}–${last.toLocaleString("en-US")} ` +
    `of ${totalLabel(total, totalIsLowerBound)}`
  );
}
